/* tslint:disable */
/* eslint-disable */
import { Injectable } from '@angular/core';
import { HttpClient, HttpResponse, HttpContext } from '@angular/common/http';
import { BaseService } from '../base-service';
import { ApiConfiguration } from '../api-configuration';
import { StrictHttpResponse } from '../strict-http-response';
import { RequestBuilder } from '../request-builder';
import { Observable } from 'rxjs';
import { map, filter } from 'rxjs/operators';

import { BasketDiscountCreateRequest } from '../models/basket-discount-create-request';
import { BasketDiscountCreateResponse } from '../models/basket-discount-create-response';
import { BasketDiscountUpdateRequest } from '../models/basket-discount-update-request';
import { BulkQuantityDiscountSynchronizeRequest } from '../models/bulk-quantity-discount-synchronize-request';
import { BulkQuantityDiscountSynchronizeResponse } from '../models/bulk-quantity-discount-synchronize-response';
import { DiscountCreateResponse } from '../models/discount-create-response';
import { DiscountUpdateRequest } from '../models/discount-update-request';
import { DiscountsDeleteRequest } from '../models/discounts-delete-request';
import { PageDiscountResponse } from '../models/page-discount-response';
import { QuantityDiscountCreateRequest } from '../models/quantity-discount-create-request';
import { QuantityDiscountCreateResponse } from '../models/quantity-discount-create-response';
import { QuantityDiscountUpdateRequest } from '../models/quantity-discount-update-request';
import { SegmentDiscountCreateRequest } from '../models/segment-discount-create-request';
import { SegmentDiscountCreateResponse } from '../models/segment-discount-create-response';
import { SegmentDiscountUpdateRequest } from '../models/segment-discount-update-request';

/**
 * Discount resource endpoints.
 */
@Injectable({
  providedIn: 'root',
})
export class DiscountsService extends BaseService {
  constructor(config: ApiConfiguration, http: HttpClient) {
    super(config, http);
  }

  /**
   * Path part for operation updateDiscount
   */
  static readonly UpdateDiscountPath = '/discounts/{discountId}';

  /**
   * Updates specified discount.
   *
   * Updates an existing discount based on its identifier.
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `updateDiscount()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  updateDiscount$Response(
    params: {
      discountId: string;
      body:
        | DiscountUpdateRequest
        | BasketDiscountUpdateRequest
        | QuantityDiscountUpdateRequest
        | SegmentDiscountUpdateRequest;
    },
    context?: HttpContext
  ): Observable<StrictHttpResponse<void>> {
    const rb = new RequestBuilder(
      this.rootUrl,
      DiscountsService.UpdateDiscountPath,
      'put'
    );
    if (params) {
      rb.path('discountId', params.discountId, {});
      rb.body(params.body, 'application/json');
    }

    return this.http
      .request(
        rb.build({
          responseType: 'text',
          accept: '*/*',
          context: context,
        })
      )
      .pipe(
        filter((r: any) => r instanceof HttpResponse),
        map((r: HttpResponse<any>) => {
          return (r as HttpResponse<any>).clone({
            body: undefined,
          }) as StrictHttpResponse<void>;
        })
      );
  }

  /**
   * Updates specified discount.
   *
   * Updates an existing discount based on its identifier.
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `updateDiscount$Response()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  updateDiscount(
    params: {
      discountId: string;
      body:
        | DiscountUpdateRequest
        | BasketDiscountUpdateRequest
        | QuantityDiscountUpdateRequest
        | SegmentDiscountUpdateRequest;
    },
    context?: HttpContext
  ): Observable<void> {
    return this.updateDiscount$Response(params, context).pipe(
      map((r: StrictHttpResponse<void>) => r.body as void)
    );
  }

  /**
   * Path part for operation bulkQuantityDiscountSynchronize
   */
  static readonly BulkQuantityDiscountSynchronizePath =
    '/discounts/bulk-synchronize';

  /**
   * Synchronizes list of quantity discounts.
   *
   * Updates quantity discounts and returns the status of the result.
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `bulkQuantityDiscountSynchronize()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  bulkQuantityDiscountSynchronize$Response(
    params: {
      body: BulkQuantityDiscountSynchronizeRequest;
    },
    context?: HttpContext
  ): Observable<StrictHttpResponse<BulkQuantityDiscountSynchronizeResponse>> {
    const rb = new RequestBuilder(
      this.rootUrl,
      DiscountsService.BulkQuantityDiscountSynchronizePath,
      'put'
    );
    if (params) {
      rb.body(params.body, 'application/json');
    }

    return this.http
      .request(
        rb.build({
          responseType: 'json',
          accept: 'application/json',
          context: context,
        })
      )
      .pipe(
        filter((r: any) => r instanceof HttpResponse),
        map((r: HttpResponse<any>) => {
          return r as StrictHttpResponse<BulkQuantityDiscountSynchronizeResponse>;
        })
      );
  }

  /**
   * Synchronizes list of quantity discounts.
   *
   * Updates quantity discounts and returns the status of the result.
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `bulkQuantityDiscountSynchronize$Response()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  bulkQuantityDiscountSynchronize(
    params: {
      body: BulkQuantityDiscountSynchronizeRequest;
    },
    context?: HttpContext
  ): Observable<BulkQuantityDiscountSynchronizeResponse> {
    return this.bulkQuantityDiscountSynchronize$Response(params, context).pipe(
      map(
        (r: StrictHttpResponse<BulkQuantityDiscountSynchronizeResponse>) =>
          r.body as BulkQuantityDiscountSynchronizeResponse
      )
    );
  }

  /**
   * Path part for operation getDiscountsList
   */
  static readonly GetDiscountsListPath = '/discounts';

  /**
   * Gets discount list with given type.
   *
   * Finds and returns discounts only for the selected site context.
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `getDiscountsList()` instead.
   *
   * This method doesn't expect any request body.
   */
  getDiscountsList$Response(
    params: {
      discountType: 'VIP' | 'SGM' | 'QNT' | 'BSK';

      /**
       * Zero-based page index (0..N)
       */
      page?: number;

      /**
       * The size of the page to be returned
       */
      size?: number;

      /**
       * Sorting criteria in the format: property,(asc|desc). Default sort order is ascending. Multiple sort criteria are supported.
       */
      sort?: Array<string>;
    },
    context?: HttpContext
  ): Observable<StrictHttpResponse<PageDiscountResponse>> {
    const rb = new RequestBuilder(
      this.rootUrl,
      DiscountsService.GetDiscountsListPath,
      'get'
    );
    if (params) {
      rb.query('discountType', params.discountType, {});
      rb.query('page', params.page, {});
      rb.query('size', params.size, {});
      rb.query('sort', params.sort, {});
    }

    return this.http
      .request(
        rb.build({
          responseType: 'json',
          accept: 'application/json',
          context: context,
        })
      )
      .pipe(
        filter((r: any) => r instanceof HttpResponse),
        map((r: HttpResponse<any>) => {
          return r as StrictHttpResponse<PageDiscountResponse>;
        })
      );
  }

  /**
   * Gets discount list with given type.
   *
   * Finds and returns discounts only for the selected site context.
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `getDiscountsList$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  getDiscountsList(
    params: {
      discountType: 'VIP' | 'SGM' | 'QNT' | 'BSK';

      /**
       * Zero-based page index (0..N)
       */
      page?: number;

      /**
       * The size of the page to be returned
       */
      size?: number;

      /**
       * Sorting criteria in the format: property,(asc|desc). Default sort order is ascending. Multiple sort criteria are supported.
       */
      sort?: Array<string>;
    },
    context?: HttpContext
  ): Observable<PageDiscountResponse> {
    return this.getDiscountsList$Response(params, context).pipe(
      map(
        (r: StrictHttpResponse<PageDiscountResponse>) =>
          r.body as PageDiscountResponse
      )
    );
  }

  /**
   * Path part for operation createDiscount
   */
  static readonly CreateDiscountPath = '/discounts';

  /**
   * Creates a new discount entry.
   *
   * Creates a new discount and returns the saved resource identifier.
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `createDiscount()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  createDiscount$Response(
    params: {
      body:
        | BasketDiscountCreateRequest
        | QuantityDiscountCreateRequest
        | SegmentDiscountCreateRequest;
    },
    context?: HttpContext
  ): Observable<
    StrictHttpResponse<
      | DiscountCreateResponse
      | BasketDiscountCreateResponse
      | QuantityDiscountCreateResponse
      | SegmentDiscountCreateResponse
    >
  > {
    const rb = new RequestBuilder(
      this.rootUrl,
      DiscountsService.CreateDiscountPath,
      'post'
    );
    if (params) {
      rb.body(params.body, 'application/json');
    }

    return this.http
      .request(
        rb.build({
          responseType: 'json',
          accept: 'application/json',
          context: context,
        })
      )
      .pipe(
        filter((r: any) => r instanceof HttpResponse),
        map((r: HttpResponse<any>) => {
          return r as StrictHttpResponse<
            | DiscountCreateResponse
            | BasketDiscountCreateResponse
            | QuantityDiscountCreateResponse
            | SegmentDiscountCreateResponse
          >;
        })
      );
  }

  /**
   * Creates a new discount entry.
   *
   * Creates a new discount and returns the saved resource identifier.
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `createDiscount$Response()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  createDiscount(
    params: {
      body:
        | BasketDiscountCreateRequest
        | QuantityDiscountCreateRequest
        | SegmentDiscountCreateRequest;
    },
    context?: HttpContext
  ): Observable<
    | DiscountCreateResponse
    | BasketDiscountCreateResponse
    | QuantityDiscountCreateResponse
    | SegmentDiscountCreateResponse
  > {
    return this.createDiscount$Response(params, context).pipe(
      map(
        (
          r: StrictHttpResponse<
            | DiscountCreateResponse
            | BasketDiscountCreateResponse
            | QuantityDiscountCreateResponse
            | SegmentDiscountCreateResponse
          >
        ) =>
          r.body as
            | DiscountCreateResponse
            | BasketDiscountCreateResponse
            | QuantityDiscountCreateResponse
            | SegmentDiscountCreateResponse
      )
    );
  }

  /**
   * Path part for operation deleteDiscounts
   */
  static readonly DeleteDiscountsPath = '/discounts';

  /**
   * Delete discounts.
   *
   * Delete specified discounts.
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `deleteDiscounts()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  deleteDiscounts$Response(
    params: {
      body: DiscountsDeleteRequest;
    },
    context?: HttpContext
  ): Observable<StrictHttpResponse<void>> {
    const rb = new RequestBuilder(
      this.rootUrl,
      DiscountsService.DeleteDiscountsPath,
      'delete'
    );
    if (params) {
      rb.body(params.body, 'application/json');
    }

    return this.http
      .request(
        rb.build({
          responseType: 'text',
          accept: '*/*',
          context: context,
        })
      )
      .pipe(
        filter((r: any) => r instanceof HttpResponse),
        map((r: HttpResponse<any>) => {
          return (r as HttpResponse<any>).clone({
            body: undefined,
          }) as StrictHttpResponse<void>;
        })
      );
  }

  /**
   * Delete discounts.
   *
   * Delete specified discounts.
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `deleteDiscounts$Response()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  deleteDiscounts(
    params: {
      body: DiscountsDeleteRequest;
    },
    context?: HttpContext
  ): Observable<void> {
    return this.deleteDiscounts$Response(params, context).pipe(
      map((r: StrictHttpResponse<void>) => r.body as void)
    );
  }

  /**
   * Path part for operation createOrUpdateDiscounts
   */
  static readonly CreateOrUpdateDiscountsPath = '/discounts/bulk';

  /**
   * Creates and updates multiple discount entries.
   *
   * Discounts with empty id are created, otherwise they are updated.
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `createOrUpdateDiscounts()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  createOrUpdateDiscounts$Response(
    params: {
      body: Array<
        | BasketDiscountCreateRequest
        | QuantityDiscountCreateRequest
        | SegmentDiscountCreateRequest
      >;
    },
    context?: HttpContext
  ): Observable<
    StrictHttpResponse<
      Array<
        | DiscountCreateResponse
        | BasketDiscountCreateResponse
        | QuantityDiscountCreateResponse
        | SegmentDiscountCreateResponse
      >
    >
  > {
    const rb = new RequestBuilder(
      this.rootUrl,
      DiscountsService.CreateOrUpdateDiscountsPath,
      'post'
    );
    if (params) {
      rb.body(params.body, 'application/json');
    }

    return this.http
      .request(
        rb.build({
          responseType: 'json',
          accept: 'application/json',
          context: context,
        })
      )
      .pipe(
        filter((r: any) => r instanceof HttpResponse),
        map((r: HttpResponse<any>) => {
          return r as StrictHttpResponse<
            Array<
              | DiscountCreateResponse
              | BasketDiscountCreateResponse
              | QuantityDiscountCreateResponse
              | SegmentDiscountCreateResponse
            >
          >;
        })
      );
  }

  /**
   * Creates and updates multiple discount entries.
   *
   * Discounts with empty id are created, otherwise they are updated.
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `createOrUpdateDiscounts$Response()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  createOrUpdateDiscounts(
    params: {
      body: Array<
        | BasketDiscountCreateRequest
        | QuantityDiscountCreateRequest
        | SegmentDiscountCreateRequest
      >;
    },
    context?: HttpContext
  ): Observable<
    Array<
      | DiscountCreateResponse
      | BasketDiscountCreateResponse
      | QuantityDiscountCreateResponse
      | SegmentDiscountCreateResponse
    >
  > {
    return this.createOrUpdateDiscounts$Response(params, context).pipe(
      map(
        (
          r: StrictHttpResponse<
            Array<
              | DiscountCreateResponse
              | BasketDiscountCreateResponse
              | QuantityDiscountCreateResponse
              | SegmentDiscountCreateResponse
            >
          >
        ) =>
          r.body as Array<
            | DiscountCreateResponse
            | BasketDiscountCreateResponse
            | QuantityDiscountCreateResponse
            | SegmentDiscountCreateResponse
          >
      )
    );
  }
}
